import React, { useState } from 'react';
import {
	CircularProgress,
	IconButton,
	Link,
	Stack,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import { Formik } from 'formik';
import { showToast } from '@/features/toast/toastSlice';
import { useObjectContext } from '@/hooks/useObjectContext';
import {
	useListDebtorRemindersQuery,
	useUpdateDebtorReminderMutation,
} from '@/features/deals/debtorAutomationApi';
import { FolderSelectDialog } from '@/components/dialogs/FolderSelectDialog';
import { useMsGraph } from '@/hooks/useMsGraph';
import { useBannerColours } from '@/hooks/useBannerColours';

const getFileNameFromDownloadUrl = (url, fallback = 'Debtor Reminder') => {
	try {
		const withoutQuery = decodeURIComponent(url).split('?')[0];
		const afterBucketPath = withoutQuery.split('/o/')[1] ?? withoutQuery;
		return afterBucketPath.split('/').pop() || fallback;
	} catch {
		return fallback;
	}
};

// Only reminders that failed solely on the SharePoint step have a Firebase-hosted
// file worth retrying — a Firebase failure means there's nothing to fetch.
const isSharepointOnlyFailure = (reminder) => {
	const reasons = reminder.failureReasons ?? [];
	return reasons.includes('sharepoint') && !reasons.includes('firebase');
};

const formatFailureSummary = (reminders) => {
	const counts = reminders.reduce((acc, reminder) => {
		acc[reminder.channel] = (acc[reminder.channel] ?? 0) + 1;
		return acc;
	}, {});

	const parts = Object.entries(counts)
		.filter(([channel]) => ['sms', 'email'].includes(channel))
		.map(([channel, count]) => `${count} sent debtor reminder ${channel}`);

	return `${parts.join(' and ')} failed to save to Sharepoint.`;
};

const DebtorRemindersBanner = () => {
	const dispatch = useDispatch();
	const { dealId, dealRow } = useObjectContext();
	const { uploadFileAndRetry } = useMsGraph();
	const theme = useTheme();
	const { backgroundColor, border, textColor } = useBannerColours('error');

	const [folderDialogOpen, setFolderDialogOpen] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [isDismissing, setIsDismissing] = useState(false);

	const { data: debtorReminders } = useListDebtorRemindersQuery(
		{ dealId, status: 'failed' },
		{ skip: !dealId },
	);

	const [updateDebtorReminder] = useUpdateDebtorReminderMutation();

	const failedReminders = (debtorReminders ?? []).filter(isSharepointOnlyFailure);

	if (!dealId || failedReminders.length === 0) return null;

	const isBusy = isSaving || isDismissing;

	const handleFolderConfirmed = async ({ selectedFolder, selectedDrive }) => {
		setFolderDialogOpen(false);
		setIsSaving(true);

		let successCount = 0;
		let failureCount = 0;

		for (const reminder of failedReminders) {
			try {
				const response = await fetch(reminder.downloadUrl);
				const blob = await response.blob();
				const fileName =
					reminder.fileName || getFileNameFromDownloadUrl(reminder.downloadUrl);
				const file = new File([blob], fileName, {
					type: blob.type || 'application/octet-stream',
				});

				const uploaded = await uploadFileAndRetry({
					driveId: selectedDrive?.parentReference?.driveId,
					itemId: selectedFolder,
					file,
					silent: true,
				});

				if (!uploaded) {
					throw new Error('Upload to Sharepoint failed');
				}

				await updateDebtorReminder({
					dealId,
					docId: reminder.id,
					status: 'success',
				}).unwrap();

				successCount += 1;
			} catch (e) {
				console.error(
					'[DebtorRemindersBanner] Failed to save reminder file to Sharepoint',
					e,
				);
				failureCount += 1;
			}
		}

		setIsSaving(false);

		if (failureCount === 0) {
			dispatch(
				showToast({
					message: `Saved ${successCount} debtor reminder file${successCount === 1 ? '' : 's'} to Sharepoint`,
					success: true,
				}),
			);
		} else {
			dispatch(
				showToast({
					message: `Saved ${successCount}, failed to save ${failureCount} debtor reminder file${
						failureCount === 1 ? '' : 's'
					} to SharePoint`,
					error: true,
				}),
			);
		}
	};

	const handleDismiss = async () => {
		setIsDismissing(true);
		try {
			await Promise.all(
				failedReminders.map((reminder) =>
					updateDebtorReminder({
						dealId,
						docId: reminder.id,
						status: 'dismissed',
					}).unwrap(),
				),
			);
		} catch (e) {
			console.error('[DebtorRemindersBanner] Failed to dismiss reminders', e);
			dispatch(
				showToast({
					message: 'Failed to dismiss debtor reminder notice: please try again',
					error: true,
				}),
			);
		} finally {
			setIsDismissing(false);
		}
	};

	return (
		<Stack
			direction='row'
			spacing={1}
			sx={{
				alignItems: 'center',
				justifyContent: 'space-between',
				px: 1,
				borderRadius: '4px',
				backgroundColor,
				border,
			}}
		>
			<Typography variant='task_updated' sx={{ color: textColor }}>
				{formatFailureSummary(failedReminders)}{' '}
				{isSaving ? (
					'Saving...'
				) : (
					<Link
						component='button'
						type='button'
						aria-label='Save debtor reminder files to Sharepoint'
						disabled={isBusy}
						onClick={() => setFolderDialogOpen(true)}
						sx={{
							color: theme.palette.primary.main,
							font: 'inherit',
							verticalAlign: 'baseline',
						}}
					>
						Click here
					</Link>
				)}{' '}
				to try save again.
			</Typography>

			<Stack direction='row' spacing={0.5} sx={{ alignItems: 'center' }}>
				<Tooltip title='Dismiss'>
					<span>
						<IconButton
							size='small'
							aria-label='Dismiss debtor reminder notice'
							disabled={isBusy}
							onClick={handleDismiss}
						>
							{isDismissing ? (
								<CircularProgress size='16px' />
							) : (
								<CloseIcon sx={{ fontSize: '18px' }} />
							)}
						</IconButton>
					</span>
				</Tooltip>
			</Stack>

			<Formik
				initialValues={{
					deal: dealRow,
					selectedFolder: null,
					selectedDrive: null,
					folderPath: '',
				}}
				onSubmit={() => {}}
			>
				<FolderSelectDialog
					initialItemId={dealRow?.driveId}
					open={folderDialogOpen}
					handleClose={() => setFolderDialogOpen(false)}
					onConfirm={handleFolderConfirmed}
				/>
			</Formik>
		</Stack>
	);
};

export default DebtorRemindersBanner;
